/**
 * Tally connector health — rebuilt as an operations hub: "is it healthy, what is stuck, what do I do".
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero (through `components/detail-parts.tsx`'s `PageHero`), its tile coloured by health;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-overview.tsx
 *   the bordered "what to do" callout under the diagnosis;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the figure cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks for the reported facts, schedules and the sync-scope rows;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/executive/stats-cards.tsx
 *   the figure row's layout.
 *
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 *
 * Health colour is the API's own escalation, read the way the dashboard's `AgentHeartbeatTile`
 * reads it: `alertRaised` (computed server-side against the heartbeat period and business hours) is
 * red; `alive` is green; `not_configured` is blue — setup, not a fault; anything else quiet without
 * an alert (silence outside business hours) is orange. No threshold is evaluated here.
 */
import type { ReactNode } from "react";
import { Link, useLoaderData, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiArrowRightBold as ArrowRight,
  PiArrowsLeftRightBold as BothWays,
  PiBuildingsDuotone as CompanyIcon,
  PiCalendarCheckDuotone as ScheduleIcon,
  PiClockDuotone as Clock,
  PiCloudWarningDuotone as ServerCrash,
  PiCoinsDuotone as ReceiptsIcon,
  PiCpuDuotone as InstanceIcon,
  PiFileMinusDuotone as CreditNoteIcon,
  PiGearSixDuotone as ConfigIcon,
  PiHeartbeatDuotone as HeartbeatIcon,
  PiInfoDuotone as InfoIcon,
  PiKeyDuotone as KeyIcon,
  PiPackageDuotone as StockIcon,
  PiPlugsConnectedDuotone as PlugIcon,
  PiProhibitDuotone as NotSyncedIcon,
  PiReceiptDuotone as InvoiceIcon,
  PiShieldWarningDuotone as ShieldOff,
  PiSunHorizonDuotone as HoursIcon,
  PiTimerDuotone as TimerIcon,
  PiTrayDuotone as Inbox,
  PiUsersDuotone as CustomersIcon,
  PiWarningDuotone as AlertTriangle,
} from "react-icons/pi";
import { Alert, Badge, Button, Text } from "rizzui";
import { CaveatBanner, MetricCard, WidgetCard, cn } from "@steelyard/theme";
import { CardTitle, CopyButton, InfoBlock, PageHero } from "../../../components/detail-parts";
import { can } from "../../../lib/permissions";
import { getAgentStatus, getSyncQueue, getTallyConfig, formatMoment, agentStateLabel, cronLabel, durationLabel, type AgentStatus, type TallyConfig } from "../../../lib/services/tallySync";
import type { AccountSummary, PermissionState } from "../../../types";

/**
 * Ported from apps/admin/app/(app)/integrations/tally/page.tsx. The connector agent is the only
 * component that touches Tally (D-17) — everything else here is downstream of whether it runs.
 * Always fetched fresh (no caching): a cached heartbeat is worse than a slow one.
 */
const STATE_COPY: Record<string, { headline: string; detail: string; action: string }> = {
  alive: {
    headline: "The agent is reporting.",
    detail: "It has checked in within the configured heartbeat period.",
    action: "Nothing to do.",
  },
  silent: {
    headline: "The agent has stopped reporting.",
    detail:
      "It reported before and has now gone quiet, which usually means the Tally machine is off or has lost its network.",
    action: "Check the machine the connector agent runs on. Its key is not in question.",
  },
  auth_failing: {
    headline: "The agent is running and being refused.",
    detail:
      "The machine is up and reaching the portal, and the portal is rejecting its key. That is a fault at any hour, unlike silence, which outside business hours is often expected.",
    action: "Reconfigure the agent with a valid key, or rotate the key and set the new one.",
  },
  not_configured: {
    headline: "No agent has ever reported.",
    detail:
      "This is not a failure anybody caused. No connector agent has been registered and connected yet, so there is no machine to check and no silence to explain.",
    action: "Issue an agent key and configure the connector on the client's Tally machine.",
  },
};

const STUB_COPY = {
  headline: "The agent is reporting, but it is a stub.",
  detail:
    "It is running its stub gateway: every item is reported as sent and nothing is posted to Tally. This is expected in Development and Test only.",
  action: "In a real environment, remove the stub override and configure the connector against the client's Tally.",
};

type Health = { word: string; badge: "success" | "danger" | "warning" | "info"; tile: string; tint: string; callout: string };

function healthOf(agent: AgentStatus): Health {
  // A stub agent heartbeats happily and posts nothing, so "alive" must never read as healthy (D-20).
  if (agent.stubbed && !agent.alertRaised)
    return { word: "Stubbed agent", badge: "warning", tile: "bg-orange-lighter text-orange-dark", tint: "bg-orange-lighter text-orange-dark", callout: "border-s-orange bg-orange-lighter/40" };
  if (agent.alertRaised)
    return { word: "Needs attention", badge: "danger", tile: "bg-red", tint: "bg-red-lighter text-red-dark", callout: "border-s-red bg-red-lighter/40" };
  if (agent.state === "alive")
    return { word: "Reporting", badge: "success", tile: "bg-green", tint: "bg-green-lighter text-green-dark", callout: "border-s-green bg-green-lighter/40" };
  if (agent.state === "not_configured")
    return { word: "Not configured", badge: "info", tile: "bg-blue", tint: "bg-blue-lighter text-blue-dark", callout: "border-s-blue bg-blue-lighter/40" };
  return {
    word: "Quiet — no alert raised",
    badge: "warning",
    tile: "bg-orange-lighter text-orange-dark",
    tint: "bg-orange-lighter text-orange-dark",
    callout: "border-s-orange bg-orange-lighter/40",
  };
}

const DIRECTION_TONE: Record<string, "secondary" | "info" | "success" | undefined> = {
  "Portal → Tally": "secondary",
  "Tally → Portal": "info",
  "Both ways": "success",
};

const SYNC_SCOPE: { what: string; direction: string; note: string; icon: ReactNode; tile: string }[] = [
  { what: "Invoices", direction: "Portal → Tally", note: "posted as accounting vouchers", icon: <InvoiceIcon aria-hidden className="size-4" />, tile: "bg-secondary-lighter text-secondary-dark" },
  { what: "Credit notes", direction: "Portal → Tally", note: "posted as accounting vouchers", icon: <CreditNoteIcon aria-hidden className="size-4" />, tile: "bg-secondary-lighter text-secondary-dark" },
  { what: "Customers", direction: "Both ways", note: "field ownership is split between Tally and the portal", icon: <CustomersIcon aria-hidden className="size-4" />, tile: "bg-green-lighter text-green-dark" },
  { what: "Receipts", direction: "Tally → Portal", note: "pulled on a schedule", icon: <ReceiptsIcon aria-hidden className="size-4" />, tile: "bg-blue-lighter text-blue-dark" },
  { what: "Stock items and quantities", direction: "Not synced", note: "the portal is the authority", icon: <StockIcon aria-hidden className="size-4" />, tile: "bg-gray-100 text-gray-500" },
];

export async function agentLoader(_args: LoaderFunctionArgs) {
  const agent = await getAgentStatus();

  // Independent, partial-failure-tolerant reads: a queue-read failure must not take down the
  // agent panel, and "0 waiting" must never be said on a failed request — it's "Unreadable" instead.
  const [queueResult, configResult] = await Promise.allSettled([getSyncQueue(), getTallyConfig()]);
  const depth = queueResult.status === "fulfilled" ? queueResult.value.unsyncedCount : null;
  const queueFailed = queueResult.status === "rejected";
  const config = configResult.status === "fulfilled" ? configResult.value : null;

  return { agent, depth, queueFailed, config };
}

export default function TallyAgentPage() {
  const { agent, depth, queueFailed, config } = useLoaderData() as {
    agent: AgentStatus;
    depth: number | null;
    queueFailed: boolean;
    config: TallyConfig | null;
  };
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const mayReadConfig = can(permissionState, "integrations.read");
  const mayAdmin = can(permissionState, "integrations.administer");

  const copy = agent.stubbed && agent.state === "alive" ? STUB_COPY : STATE_COPY[agent.state];
  const running = agent.state === "alive";
  const health = healthOf(agent);
  // A key is the fix for exactly these two states, so that is when rotating one is the primary action.
  const keyIsTheFix = agent.state === "auth_failing" || agent.state === "not_configured";

  const stateIcon = running ? (
    <HeartbeatIcon aria-hidden className="size-5" />
  ) : agent.state === "auth_failing" ? (
    <ShieldOff aria-hidden className="size-5" />
  ) : agent.state === "not_configured" ? (
    <Inbox aria-hidden className="size-5" />
  ) : (
    <ServerCrash aria-hidden className="size-5" />
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Integrations" }, { name: "Tally Agent" }]}
        icon={<PlugIcon aria-hidden className="size-9" />}
        iconClassName={health.tile}
        title="Tally Agent"
        description="The connector agent on the client's Tally machine is the only thing that talks to Tally. Everything on this page is downstream of whether it runs."
        meta={
          <>
            <Badge variant="flat" color={health.badge} rounded="pill" className="gap-1.5 font-semibold">
              <Badge renderAsDot color={health.badge} size="sm" />
              {health.word}
            </Badge>
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <Clock aria-hidden className="size-4" />
              {agent.lastHeartbeatAt ? `Last heard ${formatMoment(agent.lastHeartbeatAt)}` : "Never heard from"}
            </Badge>
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <HoursIcon aria-hidden className="size-4" />
              {agent.withinBusinessHours ? "Inside business hours" : "Outside business hours"}
            </Badge>
          </>
        }
        actions={
          <>
            <Link to="/integrations/tally/queue">
              <Button as="span" variant="outline">
                <Inbox aria-hidden className="me-1.5 size-4 text-secondary" />
                Sync queue
              </Button>
            </Link>
            {mayReadConfig ? (
              <Link to="/integrations/tally/config">
                <Button as="span" variant="outline">
                  <ConfigIcon aria-hidden className="me-1.5 size-4 text-blue" />
                  Configuration
                </Button>
              </Link>
            ) : null}
            {mayAdmin ? (
              <Link to="/integrations/tally/rotate-key">
                <Button as="span" variant={keyIsTheFix ? "solid" : "outline"}>
                  <KeyIcon aria-hidden className="me-1.5 size-4" />
                  Rotate agent key
                </Button>
              </Link>
            ) : null}
          </>
        }
      />

      {agent.alertRaised ? (
        <Alert color="danger" variant="flat" bar icon={<AlertTriangle aria-hidden className="size-5" />}>
          <div role="alert" className="flex flex-col gap-1">
            <p className="font-semibold text-red-dark">Alert: the Tally connector needs attention now.</p>
            <p className="text-sm">Silence inside business hours is a fault — the steps below say where to look.</p>
          </div>
        </Alert>
      ) : null}

      {/* ── Diagnosis: what state it is in, what that means, and what to do ─────────────────── */}
      <WidgetCard
        rounded="xl"
        title={
          <CardTitle icon={stateIcon} tile={health.tint}>
            {copy?.headline ?? `The service reports state "${agent.state}".`}
          </CardTitle>
        }
        titleClassName="text-lg sm:text-xl"
        headerClassName="items-center"
        description={
          <Text className="mt-2 max-w-3xl text-sm text-gray-600">
            {copy?.detail ??
              "This app does not recognise that state and is showing it as the service spelled it, rather than folding it into one it does recognise."}
          </Text>
        }
      >
        <div className={cn("mt-5 rounded-lg border border-s-4 border-muted px-4 py-3", health.callout)}>
          <Text className="text-xs font-semibold uppercase tracking-wider text-gray-700">What to do</Text>
          <p className="mt-1 text-sm font-medium text-gray-900">{copy?.action ?? "Report the unrecognised state — this app has no advice for it."}</p>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <InfoBlock icon={<HeartbeatIcon aria-hidden className="size-4" />} tile={health.tint} label="State">
            <Text className="font-medium text-gray-900">{agentStateLabel(agent.state)}</Text>
          </InfoBlock>
          <InfoBlock icon={<Clock aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Last heartbeat">
            <Text className="text-sm text-gray-900">{agent.lastHeartbeatAt ? formatMoment(agent.lastHeartbeatAt) : "never — no agent has ever checked in"}</Text>
          </InfoBlock>
          <InfoBlock icon={<CompanyIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Company reported">
            <Text className="text-sm text-gray-900">{agent.companyName || "not reported"}</Text>
          </InfoBlock>
          <InfoBlock icon={<InvoiceIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Tally version reported">
            <Text className="text-sm text-gray-900">{agent.tallyVersionReported ?? "not reported"}</Text>
          </InfoBlock>
          <InfoBlock icon={<InstanceIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Agent instance">
            {agent.agentInstance ? (
              <span className="flex items-center gap-1">
                <Text className="break-all font-mono text-sm text-gray-900">{agent.agentInstance}</Text>
                <CopyButton value={agent.agentInstance} label="Copy agent instance" />
              </span>
            ) : (
              <Text className="text-sm text-gray-900">none registered</Text>
            )}
          </InfoBlock>
          <InfoBlock icon={<HoursIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Within business hours">
            <Text className="text-sm text-gray-900">{agent.withinBusinessHours ? "yes" : "no — silence outside business hours does not raise an alert"}</Text>
          </InfoBlock>
          <InfoBlock icon={<TimerIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Configured alert period">
            <Text className="text-sm text-gray-900">{config ? durationLabel(config.heartbeatAlertPeriod) : "configured server-side; visible on the configuration screen"}</Text>
          </InfoBlock>
        </div>
      </WidgetCard>

      {running ? null : (
        <Alert color="info" variant="flat" bar icon={<InfoIcon aria-hidden className="size-5" />}>
          <div role="note">
            <p className="font-semibold text-blue-dark">Work is queueing, and nothing is lost.</p>
            <p className="text-sm">
              Invoices and credit notes raised while the agent is down stay in the sync queue and post when it comes back.
              Carry on raising them.
            </p>
          </div>
        </Alert>
      )}

      {/* ── Figures ─────────────────────────────────────────────────────────────────────── */}
      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Waiting for the agent"
          icon={queueFailed ? <ServerCrash aria-hidden className="size-6" /> : <Inbox aria-hidden className="size-6" />}
          iconClassName={cn("text-white shadow-sm", queueFailed ? "bg-red" : "bg-secondary")}
          metric={queueFailed ? <span className="text-red-dark">Unreadable</span> : depth === null ? <span className="text-gray-500">Not reported</span> : depth}
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              {queueFailed ? "The queue could not be read, so this is not zero — it is unknown." : "Records the agent has not yet posted to Tally."}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        >
          <Link to="/integrations/tally/queue" className="mt-4 inline-flex items-center gap-1 border-t border-dashed border-muted pt-3 text-sm font-medium text-secondary hover:underline">
            Open the sync queue
            <ArrowRight aria-hidden className="size-3.5" />
          </Link>
        </MetricCard>

        <MetricCard
          title="Authentication failures in a row"
          icon={<ShieldOff aria-hidden className="size-6" />}
          iconClassName={cn("text-white shadow-sm", agent.state === "auth_failing" ? "bg-red" : "bg-blue")}
          metric={<span className={agent.state === "auth_failing" ? "text-red-dark" : undefined}>{String(agent.consecutiveAuthFailures)}</span>}
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              A rising count after a key rotation is a botched rotation, visible in minutes rather than at month end.
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />

        <MetricCard
          title="Heartbeat alert period"
          icon={<TimerIcon aria-hidden className="size-6" />}
          iconClassName="bg-green text-white shadow-sm"
          metric={config ? durationLabel(config.heartbeatAlertPeriod) : <span className="text-gray-500">Not shown here</span>}
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              {config ? "Silence longer than this, inside business hours, raises the alert." : "Configured server-side; visible on the configuration screen."}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-12">
        {/* ── Pulls ──────────────────────────────────────────────────────────────────────── */}
        <WidgetCard
          rounded="xl"
          className="xl:col-span-5"
          title={
            <CardTitle icon={<ScheduleIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
              Pulls, per data type
            </CardTitle>
          }
          headerClassName="items-center"
          description={<Text className="mt-2 text-sm text-gray-500">Invoices can keep posting while the receipts pull has been dead for weeks.</Text>}
        >
          <div className="mt-5 flex flex-col gap-4">
            <InfoBlock icon={<CustomersIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Customer pull">
              <PullSchedule cron={config?.customerPullSchedule} />
            </InfoBlock>
            <InfoBlock icon={<ReceiptsIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Receipt pull">
              <PullSchedule cron={config?.receiptPullSchedule} />
            </InfoBlock>
          </div>

          <Alert color="warning" variant="flat" className="mt-5">
            <div role="note">
              <p className="font-semibold">
                The last <em>successful</em> pull per type is not yet on this screen.
              </p>
              <p className="text-sm">
                The schedules above show when a pull is <em>due</em>, not when one last <em>worked</em>.
              </p>
            </div>
          </Alert>
        </WidgetCard>

        {/* ── Scope ──────────────────────────────────────────────────────────────────────── */}
        <div className="xl:col-span-7">
          <CaveatBanner
            caveat="Stock items and quantities are not synced with Tally. The portal is the authority for stock."
            action={
              <span>
                Retiring Tally&rsquo;s stock reports at cutover, rather than running both, is recommended. Two stock records
                that nothing reconciles will disagree, and the disagreement is discovered by whoever trusts the wrong one.
              </span>
            }
          >
            <WidgetCard
              rounded="xl"
              title={
                <CardTitle icon={<BothWays aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
                  What is synced, and in which direction
                </CardTitle>
              }
              headerClassName="items-center"
            >
              <ul className="mt-5 flex flex-col divide-y divide-muted">
                {SYNC_SCOPE.map((entry) => (
                  <li key={entry.what} className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <span className={cn("flex size-9 shrink-0 items-center justify-center rounded-lg", entry.tile)}>{entry.icon}</span>
                    <div className="min-w-0 flex-1">
                      <Text className="font-medium text-gray-900">{entry.what}</Text>
                      <Text className="text-sm text-gray-500">{entry.note}</Text>
                    </div>
                    {DIRECTION_TONE[entry.direction] ? (
                      <Badge variant="flat" color={DIRECTION_TONE[entry.direction]} rounded="pill">
                        {entry.direction}
                      </Badge>
                    ) : (
                      <Badge variant="flat" rounded="pill" className="gap-1 bg-gray-100 text-gray-600">
                        <NotSyncedIcon aria-hidden className="size-3.5" />
                        {entry.direction}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-5 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:bg-gray-100/40">
                A customer who registered but has never ordered is not in Tally at all. The ledger is created on their first
                successful order, not at registration — so &ldquo;no ledger&rdquo; is a normal state, not a missing record.
              </p>
            </WidgetCard>
          </CaveatBanner>
        </div>
      </div>
    </div>
  );
}

function PullSchedule({ cron }: { cron?: string }) {
  if (!cron) return <Text className="text-sm text-gray-500">on the configuration screen</Text>;
  return (
    <>
      <Text className="font-medium text-gray-900">{cronLabel(cron)}</Text>
      <Text className="font-mono text-xs text-gray-500">{cron}</Text>
    </>
  );
}
